import { useCallback, useEffect, useState } from 'react';
import type { AnswerValue } from '@voice2me/shared';
import type { AnswersState } from './SurveyForm.js';

/**
 * Draft answers live in localStorage only. There is deliberately no server-side draft: in
 * anonymous mode, storing a partly-finished response against a user would create exactly the
 * link the survey promises not to keep.
 */
export function useDraftAnswers(surveyKey: string) {
  const storageKey = `v2m:draft:${surveyKey}`;
  const [answers, setAnswers] = useState<AnswersState>({});

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(storageKey);
      setAnswers(stored ? (JSON.parse(stored) as AnswersState) : {});
    } catch {
      setAnswers({});
    }
  }, [storageKey]);

  const setAnswer = useCallback(
    (questionId: string, value: AnswerValue | undefined) => {
      setAnswers((previous) => {
        const next = { ...previous };
        if (value === undefined) delete next[questionId];
        else next[questionId] = value;
        try {
          window.localStorage.setItem(storageKey, JSON.stringify(next));
        } catch {
          // A full or disabled localStorage must not stop someone answering.
        }
        return next;
      });
    },
    [storageKey],
  );

  const clearDraft = useCallback(() => {
    try {
      window.localStorage.removeItem(storageKey);
    } catch {
      // Nothing useful to do.
    }
    setAnswers({});
  }, [storageKey]);

  return { answers, setAnswer, clearDraft };
}
